import {
  ReactNode,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  useEffect,
} from 'react';

import { META_COLLECT_API } from '../../../api';
import { ScheduleCollect } from '../../../types/ScheduleCollect';
import { deformatDate } from '../../../utils/date/deformatDate';
import { useSigninContext } from '../../AuthProviderPages/Signin';
import { usePageContext } from '../../usePage';

interface IContextProps {
  children: ReactNode;
}

interface IContext {
  openSideModalFilterScheduleCollect: boolean;
  openSideModalFilterScheduleCollectAction: () => void;

  DFNOMECARRETEIRO: string;
  setDFNOMECARRETEIRO: Dispatch<SetStateAction<string>>;
  DFORDEMCOLETA: string;
  setDFORDEMCOLETA: Dispatch<SetStateAction<string>>;
  DFNOMELINHA: string;
  setDFNOMELINHA: Dispatch<SetStateAction<string>>;
  DFPLACAVEICULO: string;
  setDFPLACAVEICULO: Dispatch<SetStateAction<string>>;
  DFDATACOLETA: string;
  setDFDATACOLETA: Dispatch<SetStateAction<string>>;
  DFDATASAIDA: string;
  setDFDATASAIDA: Dispatch<SetStateAction<string>>;
  DFIDCOLETA: string;
  setDFIDCOLETA: Dispatch<SetStateAction<string>>;
  DFDATAPROGRAMADA: string;
  setDFDATAPROGRAMADA: Dispatch<SetStateAction<string>>;

  filterScheduleCollectAction: () => void;
  cleanAllInputsAction: () => void;
  loadScheduleAction: () => void;

  openInfo: boolean;
  setOpenInfo: Dispatch<SetStateAction<boolean>>;

  selectedScheduleCollect: ScheduleCollect | undefined;
  setSelectedScheduleCollect: Dispatch<
    SetStateAction<ScheduleCollect | undefined>
  >;

  scheduleCollect: ScheduleCollect[];
  setScheduleCollect: Dispatch<SetStateAction<ScheduleCollect[]>>;

  loadingScheduleCollect: boolean;

  offset: number;
  setOfsset: Dispatch<SetStateAction<number>>;
  limit: number;
  setPage: Dispatch<SetStateAction<number>>;
  page: number;
  quantity: number;
  setHasClick: Dispatch<SetStateAction<boolean>>;
  start: number;
  setStart: Dispatch<SetStateAction<number>>;
}

const Context = createContext({} as IContext);

const ScheduleContextProvider = ({ children }: IContextProps) => {
  const [DFNOMECARRETEIRO, setDFNOMECARRETEIRO] = useState<string>('');
  const [DFORDEMCOLETA, setDFORDEMCOLETA] = useState<string>('');
  const [DFNOMELINHA, setDFNOMELINHA] = useState<string>('');
  const [DFPLACAVEICULO, setDFPLACAVEICULO] = useState<string>('');
  const [DFDATACOLETA, setDFDATACOLETA] = useState<string>('');
  const [DFDATASAIDA, setDFDATASAIDA] = useState<string>('');
  const [DFIDCOLETA, setDFIDCOLETA] = useState<string>('');
  const [DFDATAPROGRAMADA, setDFDATAPROGRAMADA] = useState<string>('');

  const [loadingScheduleCollect, setIsLoadingScheduleCollect] =
    useState<boolean>(false);
  const [
    openSideModalFilterScheduleCollect,
    setOpenSideModalFilterScheduleCollect,
  ] = useState<boolean>(false);
  const [openInfo, setOpenInfo] = useState<boolean>(false);

  const [selectedScheduleCollect, setSelectedScheduleCollect] = useState<
    ScheduleCollect | undefined
  >();
  const [scheduleCollect, setScheduleCollect] = useState<ScheduleCollect[]>([]);

  const [limit] = useState<number>(50);
  const [offset, setOfsset] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [hasClick, setHasClick] = useState<boolean>(false);
  const { path, user } = useSigninContext();
  const { selectedPage } = usePageContext();
  const [start, setStart] = useState(1);
  const Limit = `limit=${limit}`;
  const Offset = `offset=${offset}`;
  const NOMECARRETEIRO = `DFNOMECARRETEIRO=${DFNOMECARRETEIRO}`;
  const ORDEMCOLETA = `DFORDEMCOLETA=${DFORDEMCOLETA}`;
  const NOMELINHA = `DFNOMELINHA=${DFNOMELINHA}`;
  const PLACAVEICULO = `DFPLACAVEICULO=${DFPLACAVEICULO}`;
  const DATACOLETA = `DFDATACOLETA=${deformatDate(DFDATACOLETA)}`;
  const DATASAIDA = `DFDATASAIDA=${deformatDate(DFDATASAIDA)}`;
  const IDCOLETA = `DFIDCOLETA=${DFIDCOLETA}`;
  const STATUS = `DFSTATUS=P`;
  const DATAPROGRAMADA = `DFDATAPROGRAMADA=${deformatDate(DFDATAPROGRAMADA)}`;
  const IDUNIDADE = user?.DFIDUNIDADE
    ? `&DFIDUNIDADE=${user?.DFIDUNIDADE}`
    : '';

  const openSideModalFilterScheduleCollectAction = () => {
    setOpenSideModalFilterScheduleCollect(!openSideModalFilterScheduleCollect);
  };

  useEffect(() => {
    if (hasClick) {
      setIsLoadingScheduleCollect(true);
      META_COLLECT_API.get(
        `/coleta?${Limit}&${Offset}&${STATUS}&${NOMECARRETEIRO}&${ORDEMCOLETA}&${NOMELINHA}&${PLACAVEICULO}&${DATACOLETA}&${DATASAIDA}&${IDCOLETA}&${DATAPROGRAMADA}${IDUNIDADE}`,
        {
          headers: {
            path,
          },
        },
      )
        .then(response => {
          setScheduleCollect(response.data.coletas);
          setQuantity(response.data.quantidade);
          setIsLoadingScheduleCollect(false);
          setHasClick(false);
        })
        .catch(() => {
          setIsLoadingScheduleCollect(false);
          setHasClick(false);
        });
    }
  }, [hasClick]);

  const loadScheduleActionWithParams = () => {
    setIsLoadingScheduleCollect(true);
    META_COLLECT_API.get(`/coleta?${Limit}&offset=0&${STATUS}${IDUNIDADE}`, {
      headers: {
        path,
      },
    })
      .then(response => {
        setScheduleCollect(response.data.coletas);
        setQuantity(response.data.quantidade);
        setIsLoadingScheduleCollect(false);
        setStart(1);
      })
      .catch(() => {
        setIsLoadingScheduleCollect(false);
        setStart(1);
      });
  };

  const executeCleanInputs = () => {
    setDFNOMECARRETEIRO('');
    setDFORDEMCOLETA('');
    setDFNOMELINHA('');
    setDFPLACAVEICULO('');
    setDFDATACOLETA('');
    setDFDATASAIDA('');
    setOpenInfo(false);
    setDFDATAPROGRAMADA('');
  };

  const cleanAllInputsAction = () => {
    executeCleanInputs();
    openSideModalFilterScheduleCollectAction();
    loadScheduleActionWithParams();
    setPage(1);
    setStart(1);
  };

  const filterScheduleCollectAction = () => {
    setOfsset(0);
    setPage(1);
    openSideModalFilterScheduleCollectAction();
    setIsLoadingScheduleCollect(true);
    META_COLLECT_API.get(
      `/coleta?${Limit}&offset=0&${STATUS}&${NOMECARRETEIRO}&${ORDEMCOLETA}&${NOMELINHA}&${PLACAVEICULO}&${DATACOLETA}&${DATASAIDA}&${IDCOLETA}&${DATAPROGRAMADA}${IDUNIDADE}`,
      {
        headers: {
          path,
        },
      },
    )
      .then(response => {
        setScheduleCollect(response.data.coletas);
        setQuantity(response.data.quantidade);
        setIsLoadingScheduleCollect(false);
        setStart(1);
      })
      .catch(() => {
        setIsLoadingScheduleCollect(false);
        setStart(1);
      });
  };

  const loadScheduleAction = () => {
    setIsLoadingScheduleCollect(true);
    loadScheduleActionWithParams();
  };

  useEffect(() => {
    if (selectedPage.option !== 'descarga') {
      executeCleanInputs();
      setOfsset(0);
      setPage(1);
      setStart(1);
    }
  }, [selectedPage]);

  useEffect(() => {
    if (user?.DFIDUNIDADE) {
      loadScheduleAction();
    }
  }, [user]);

  return (
    <Context.Provider
      value={{
        cleanAllInputsAction,
        DFORDEMCOLETA,
        filterScheduleCollectAction,
        loadScheduleAction,
        limit,
        loadingScheduleCollect,
        offset,
        openInfo,
        openSideModalFilterScheduleCollect,
        openSideModalFilterScheduleCollectAction,
        page,
        quantity,
        setHasClick,
        setOfsset,
        setOpenInfo,
        setPage,
        setScheduleCollect,
        DFDATACOLETA,
        DFDATASAIDA,
        DFNOMECARRETEIRO,
        DFNOMELINHA,
        DFPLACAVEICULO,
        DFIDCOLETA,
        scheduleCollect,
        selectedScheduleCollect,
        setDFDATACOLETA,
        setDFDATASAIDA,
        setDFNOMECARRETEIRO,
        setDFNOMELINHA,
        setDFORDEMCOLETA,
        setDFPLACAVEICULO,
        setSelectedScheduleCollect,
        setDFIDCOLETA,
        start,
        setStart,
        DFDATAPROGRAMADA,
        setDFDATAPROGRAMADA,
      }}
    >
      {children}
    </Context.Provider>
  );
};

function useScheduleContext() {
  const context = useContext(Context);
  return context;
}

export { ScheduleContextProvider, useScheduleContext };
